import { useEffect, useMemo } from 'react';
import { TaskCenterTypes } from '@frontend/api-task-center';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useAppScopeStore } from '@frontend/scope';
import { CitationMetadata, useTaskCenterStore } from '@frontend/task-center-scope';
import { useGetTasksV2 } from './use-get-tasks-v2';

interface UseFilterPrivateTasksParams {
  status: TaskCenterTypes.TaskStatus;
  enabled: boolean;
}

export const useFilterPrivateTasks = ({ status, enabled }: UseFilterPrivateTasksParams) => {
  const { selectedLocationIds } = useAppScopeStore();
  const { shouldFilterPrivateComms } = useTaskCenterStore(['shouldFilterPrivateComms']);

  const taskQuery = useGetTasksV2({ status, enabled });

  const checkPhoneAccess = InboxLocationPhones.Hooks.useCheckUserLocationPhoneAccess(selectedLocationIds);

  const data = useMemo(() => {
    if (!taskQuery.data || !shouldFilterPrivateComms) return taskQuery.data;
    const pages = taskQuery.data.pages.map((page) => {
      if (!page.tasks) return page;
      const filteredTasks = page?.tasks.filter((task) => {
        const taskMetadata: CitationMetadata = task?.metadata ? JSON.parse(task.metadata) : undefined;
        if (!taskMetadata?.source?.locationPhone) return true;
        return checkPhoneAccess(taskMetadata.source.locationPhone);
      });
      return { ...page, tasks: filteredTasks };
    });
    return { ...taskQuery.data, pages };
  }, [shouldFilterPrivateComms, checkPhoneAccess, taskQuery.data]);

  const list = useMemo(() => {
    return data?.pages?.flatMap((page) => page.tasks).filter((task) => !!task);
  }, [data]);

  const lastPageLength = !!data?.pages?.length ? data.pages[data.pages.length - 1]?.tasks?.length || 0 : 0;

  // Auto-fetch pages until filtered results exist or no more pages
  useEffect(() => {
    if (!shouldFilterPrivateComms) return;
    if (taskQuery.isLoading || taskQuery.isFetchingNextPage) return; // avoid duplicate fetches
    if (lastPageLength) return; // we have data → stop fetching more
    if (!taskQuery.hasNextPage) return; // no more pages → stop

    taskQuery.fetchNextPage();
  }, [
    taskQuery.hasNextPage,
    taskQuery.fetchNextPage,
    taskQuery.isLoading,
    taskQuery.isFetchingNextPage,
    lastPageLength,
    shouldFilterPrivateComms,
  ]);

  const derivedLoading = taskQuery.isLoading || (lastPageLength === 0 && taskQuery.hasNextPage);

  return {
    ...taskQuery,
    list,
    ...(shouldFilterPrivateComms && { data, isLoading: derivedLoading }),
  };
};
